import { useSelector } from "react-redux";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import Box from "@mui/material/Box/Box";
import CompanySelect from "$forms/CompanySelect";
import useAuth from "$hooks/useAuth";
import { appState } from "$reducers";
import { Company, CompanyType } from "$types";
import useStyles from "./styles/RightHeader.styles";
import { CompanyServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";

const renderConfigName = (configs: { type: string }[]) => {
  if (configs?.length <= 0) {
    return "";
  }
  if (configs?.length === 1) {
    return `(Rattaché au WMS : [${configs[0].type}])`;
  }

  const types = [];
  configs?.forEach((config: any) => {
    if (!types.includes(config.type)) {
      types.push(config.type);
    }
  });

  return types.length > 1
    ? `(Rattaché aux WMS : [${types[0]} et ${types[1]}])`
    : `(Rattaché au WMS : [${types[0]}])`;
};

type Props = {
  companyId: string;
  contributorId: Company["_id"];
  catalogWMS: any;
};

function RightHeader({ companyId = "", contributorId, catalogWMS }: Props) {
  const classes = useStyles();
  const { search, pathname } = useLocation();
  const { push } = useHistory();

  const {
    isAdmin,
    isContributor: isUserContributor,
    isClient: isUserClient,
    user,
  } = useAuth();

  const filter = { isActive: true, page: 1, rowsPerPage: 50000 };
  const { data: companies } = useQuery(
    [CacheInvalidationAction.GET_COMPANIES, filter],
    () => CompanyServices.getCompanies(filter),
    {
      enabled: !!contributorId,
      staleTime: 60 * 1000,
    }
  );

  const { data: contributorCompany } = useQuery(
    [CacheInvalidationAction.GET_COMPANY_BY_ID, contributorId],
    () => CompanyServices.getCompanyById(contributorId),
    {
      enabled: !!contributorId,
      staleTime: 60 * 1000,
    }
  );

  const userCompany = user?.userInfo?.userCompany;
  const { filter: companySidebarFilter } = useSelector(appState);

  const goToCatalogueOf = useCallback(
    (companyId: string) => {
      push(`/catalogue/${companyId || ""}${search}`);
    },
    [push, search]
  );

  // Redirect to the filtered company catalogue, but not if we are already there.
  // Otherwise each visit to /catalogue remounts the full product table.
  useEffect(() => {
    const filterCompanyId = companySidebarFilter?._id;
    if (!filterCompanyId) return;

    const alreadyOnCompanyCatalogue =
      pathname === `/catalogue/${filterCompanyId}` ||
      pathname.startsWith(`/catalogue/${filterCompanyId}/`);

    if (!alreadyOnCompanyCatalogue) {
      goToCatalogueOf(filterCompanyId);
    }
  }, [companySidebarFilter?._id, pathname, goToCatalogueOf]);

  const companyOptions = useMemo(() => {
    let companyOptions = isUserClient() ? [userCompany] : [];

    if (isUserContributor()) {
      companyOptions.push(
        ...(companies?.data || []).filter(
          (c) => userCompany?.contributorOf?.includes(c._id) && c.isActive
        )
      );
    }
    const { contributorOf } = contributorCompany || {};
    // Forced "contributor" (coming from url param "contributor=")
    if (contributorOf && contributorOf.length > 0) {
      const contributorIds = contributorOf.map((c) => c._id);
      companyOptions = (companies?.data || []).filter(
        (c) => contributorIds?.includes(c._id) && c.isActive
      );
    }

    // This filter ... Remove duplicates... !
    companyOptions = companyOptions.filter(
      (item, index) => companyOptions.indexOf(item) === index
    );

    // if companyOptions is empty, return undefined
    // to ensure the CompanySelect computes its own options instead of using an empty array
    return companyOptions?.length > 0 ? companyOptions : undefined;
  }, [
    companies,
    contributorCompany,
    isUserClient,
    isUserContributor,
    userCompany,
  ]);

  if (!isAdmin() && !isUserContributor()) {
    return null;
  }
  const sideBarFilterON = !!companySidebarFilter?._id;
  return (
    <Box className={classes.headerContent}>
      <div className={classes.headerConfig}>{renderConfigName(catalogWMS)}</div>
      <Box display="flex" flex={1} justifyContent="flex-end">
        <Box width={300}>
          <CompanySelect
            className={classes.select}
            disabled={!!companySidebarFilter && isAdmin()}
            companyOptions={
              sideBarFilterON ? [companySidebarFilter] : companyOptions
            }
            rowsPerPage={99999}
            minCharsToTrigerSearch={0}
            companyTypes={[CompanyType.CLIENT]}
            ignoreSideBarCompanyFilter
            label={
              contributorCompany
                ? companyOptions?.length === 0
                  ? "Aucun client rattaché"
                  : "Entreprise cliente"
                : "Entreprise"
            }
            onChange={goToCatalogueOf}
            value={companyId || companySidebarFilter?._id}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default RightHeader;
